/*
  Homepage 3-column recs engine for Hey Jack.

  Fills the three client-side columns of the homepage recs band
  (sections/home-recs.liquid), each laid out as the shared 2x2 scroll-snap
  carousel (.hj-mfrecs__panel > .hj-mfrecs__stack) followed by a see-all tile:

    Column 1  Your Favorites      saved handles from localStorage hj_favorites
                                  (window.HeyJackFavorites). Newest first.
    Column 2  Recently Viewed     handles from localStorage hj_recently_viewed
                                  (window.HeyJackRecentlyViewed). Newest first.
    Column 3  Personalized Picks  the top-scoring products from the server pool,
                                  scored against a taste profile built from the
                                  visitor's favorites (higher weight) and
                                  recently viewed (lower weight), using the same
                                  facet matching the product page uses
                                  (heyjack.subject / color_specific / style) plus
                                  tags as a lighter signal.

  The server hands us a product pool as JSON in [data-hj-recs-pool]: each entry
  carries the display fields plus the scoring facets (subject/color/style/tags).
  The pool is also the source of the random prints shown in the empty states and
  the generic fallback for the picks column when there is no taste profile yet.

  A favorited or recently viewed product that is not in the pool (e.g. an older
  print) is still shown in its column: we fetch it from the product JSON endpoint
  the same way favorites.js / recently-viewed.js do. It just cannot contribute to
  the picks profile, since the endpoint does not return metafields.

  Cards are built with the same markup as snippets/product-card.liquid, so the
  favorites.js heart wiring (document-level delegation + a MutationObserver that
  paints injected hearts) lights them up with no extra wiring here.

  After filling a column we reset its viewport scrollLeft to 0 so it opens on the
  first panel (browser scroll-anchoring otherwise keeps the pre-rendered see-all
  tile in view, landing the carousel on the last slide), then fire a window
  resize so the section's arrow script recomputes the prev/next disabled states
  for the new panel count.
*/
(function () {
  'use strict';

  var FAV_WEIGHT = 3; // a favorite says more about taste than a glance
  var RV_WEIGHT = 1; // a recently viewed print is a lighter signal
  var TAG_FACTOR = 0.5; // tags match looser than the curated facets, so weight them down
  var MAX_CARDS = 12; // up to three 2x2 panels per column; overridden by the section's data-hj-max
  var EMPTY_PRINTS = 3; // random prints shown beside an empty-state message
  var FACETS = ['subject', 'color', 'style'];

  // ----- small shared helpers (mirrors favorites.js / recently-viewed.js) -----

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // The product JSON endpoint returns prices in cents; the server pool already
  // sends cents too, so both paths format the same way.
  function formatMoney(cents) {
    var amount = (Number(cents) || 0) / 100;
    return '$' + amount.toFixed(2);
  }

  function sizeImage(url, width) {
    if (!url) {
      return '';
    }
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + width;
  }

  // ----- product cards -----

  // Build one card element from a normalized product object
  // ({ handle, url, title, price, compareAt, image, image2 }). Same structure as
  // snippets/product-card.liquid so the favorites heart wiring picks it up.
  function buildCard(p) {
    var handle = p.handle || '';
    var url = p.url || ('/products/' + handle);
    var title = p.title || '';
    var onSale = p.compareAt && p.compareAt > p.price;

    var html = '';
    html += '<a class="hj-pcard" href="' + escapeHtml(url) + '">';
    html += '<div class="hj-pcard__panel">';
    html += '<div class="hj-pcard__badges">';
    if (onSale) {
      html += '<span class="hj-badge hj-badge--sale">On Sale</span>';
    }
    html += '</div>';
    html += '<button class="hj-pcard__fav" type="button" aria-label="Save to favorites" data-favorite-toggle data-handle="' + escapeHtml(handle) + '">';
    html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z"></path></svg>';
    html += '</button>';
    if (p.image) {
      // width/height match the 2:3 .hj-pcard__panel. The image is absolutely
      // positioned and fills the panel via object-fit, so the panel's CSS
      // aspect-ratio controls layout; these attributes give the browser an
      // up-front aspect hint and satisfy the "explicit width and height" audit.
      html += '<img class="hj-pcard__img hj-pcard__img--base" src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(title) + '" width="600" height="900" loading="lazy" />';
      if (p.image2) {
        html += '<img class="hj-pcard__img hj-pcard__img--hover" src="' + escapeHtml(p.image2) + '" alt="' + escapeHtml(title) + '" width="600" height="900" loading="lazy" />';
      }
    } else {
      html += '<div class="hj-pcard__art"></div>';
    }
    html += '</div>';
    html += '<div class="hj-pcard__info">';
    html += '<h3 class="hj-pcard__title">' + escapeHtml(title) + '</h3>';
    html += '<p class="hj-pcard__price">';
    if (onSale) {
      html += '<span class="hj-pcard__price-was">' + formatMoney(p.compareAt) + '</span>';
      html += '<span class="hj-pcard__price-now">' + formatMoney(p.price) + '</span>';
    } else {
      html += '<span class="hj-pcard__price-now">' + formatMoney(p.price) + '</span>';
    }
    html += '</p>';
    html += '</div>';
    html += '</a>';

    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.firstElementChild;
  }

  // The encouraging message tile shown in an empty column, sitting as the first
  // cell of the empty-state 2x2 panel ahead of a few random prints.
  function buildEmptyCard(title, text, cta) {
    var el = document.createElement('div');
    el.className = 'hj-recs-empty';
    el.innerHTML =
      '<span class="hj-recs-empty__title">' + escapeHtml(title) + '</span>' +
      '<span class="hj-recs-empty__text">' + escapeHtml(text) + '</span>' +
      '<span class="hj-recs-empty__cta">' + escapeHtml(cta) + '</span>';
    return el;
  }

  // ----- panel + column plumbing -----

  function buildPanel(children) {
    var panel = document.createElement('div');
    panel.className = 'hj-mfrecs__panel';
    panel.setAttribute('data-hj-panel', '');
    var stack = document.createElement('div');
    stack.className = 'hj-mfrecs__stack';
    children.forEach(function (child) {
      stack.appendChild(child);
    });
    panel.appendChild(stack);
    return panel;
  }

  // Insert freshly built panels ahead of the column's see-all marker, reset the
  // viewport to the first panel, and let the section's arrow script recompute.
  function fillColumn(col, panels) {
    var marker = col.querySelector('[data-hj-grid]');
    if (!marker) {
      return;
    }
    var viewport = marker.parentNode;
    if (!viewport) {
      return;
    }
    marker.style.display = 'none';

    // Drop any panels from a previous render (e.g. favorites re-rendering on a
    // change event) without touching the see-all tile.
    var existing = viewport.querySelectorAll('[data-hj-panel]');
    for (var i = 0; i < existing.length; i++) {
      existing[i].parentNode.removeChild(existing[i]);
    }

    panels.forEach(function (panel) {
      viewport.insertBefore(panel, marker);
    });

    // Open on the first panel. Without this, inserting panels before the
    // already-visible see-all tile makes scroll-anchoring jump to the last slide.
    viewport.scrollLeft = 0;

    try {
      window.dispatchEvent(new Event('resize'));
    } catch (e) {}
  }

  // ----- product fetching (pool first, endpoint fallback) -----

  function normalizeJs(j) {
    if (!j || !j.handle) {
      return null;
    }
    var images = j.images || [];
    return {
      handle: j.handle,
      url: '/products/' + j.handle,
      title: j.title || '',
      price: j.price,
      compareAt: j.compare_at_price,
      image: sizeImage(j.featured_image, 600),
      image2: images.length > 1 ? sizeImage(images[1], 600) : ''
    };
  }

  // Resolve a handle to a normalized product: from the pool if present, else
  // fetched fresh (so favorites / recently viewed outside the pool still show).
  function getProduct(handle, poolByHandle) {
    var p = poolByHandle[handle];
    if (p) {
      return Promise.resolve(p);
    }
    return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (j) {
        return normalizeJs(j);
      })
      .catch(function () {
        return null;
      });
  }

  function getProducts(handles, poolByHandle) {
    return Promise.all(handles.map(function (h) {
      return getProduct(h, poolByHandle);
    })).then(function (list) {
      return list.filter(Boolean);
    });
  }

  // ----- handle readers -----

  function favoriteHandles() {
    var mod = window.HeyJackFavorites;
    var list = mod && mod.list ? mod.list() : [];
    // Stored oldest first; show newest first, cap the column.
    return list.slice().reverse().filter(Boolean).slice(0, MAX_CARDS);
  }

  function recentHandles() {
    var mod = window.HeyJackRecentlyViewed;
    var list = mod && mod.list ? mod.list() : [];
    return list.slice().reverse().filter(Boolean).slice(0, MAX_CARDS);
  }

  // ----- a column of saved/viewed products, with an empty state -----

  // products: normalized products for this column (already ordered, capped).
  // When empty, render a single panel: the encouraging message tile plus a few
  // random prints from the pool.
  function renderListColumn(col, products, pool, emptyCopy) {
    if (products.length === 0) {
      var randoms = pickRandom(pool, EMPTY_PRINTS).map(buildCard);
      var children = [buildEmptyCard(emptyCopy.title, emptyCopy.text, emptyCopy.cta)].concat(randoms);
      fillColumn(col, [buildPanel(children)]);
      return;
    }
    fillColumn(col, chunkPanels(products.map(buildCard)));
  }

  // Group card elements into 2x2 panels of up to 4.
  function chunkPanels(cards) {
    var panels = [];
    for (var i = 0; i < cards.length; i += 4) {
      panels.push(buildPanel(cards.slice(i, i + 4)));
    }
    return panels;
  }

  // ----- random selection (empty states + fallback) -----

  // Fisher-Yates on a copy, take the first n. Math.random is fine in the browser.
  function pickRandom(list, n) {
    var copy = list.slice();
    for (var i = copy.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = copy[i];
      copy[i] = copy[j];
      copy[j] = tmp;
    }
    return copy.slice(0, n);
  }

  // ----- personalized scoring (mirrors the product page facet match) -----

  // Build a taste profile: for every facet value on each favorited / recently
  // viewed product found in the pool, accumulate its weight. Returns a map of
  // facet -> { value -> weight }, or null when there is no signal at all.
  function buildProfile(favSet, recentSet, poolByHandle) {
    var profile = { subject: {}, color: {}, style: {}, tags: {} };
    var any = false;

    function ingest(handle, weight) {
      var p = poolByHandle[handle];
      if (!p) {
        return;
      }
      any = true;
      FACETS.concat(['tags']).forEach(function (facet) {
        var values = p[facet] || [];
        for (var i = 0; i < values.length; i++) {
          var v = values[i];
          profile[facet][v] = (profile[facet][v] || 0) + weight;
        }
      });
    }

    favSet.forEach(function (h) {
      ingest(h, FAV_WEIGHT);
    });
    recentSet.forEach(function (h) {
      ingest(h, RV_WEIGHT);
    });

    return any ? profile : null;
  }

  // Score a pool product against the profile: curated facets at full weight,
  // tags at the lighter TAG_FACTOR.
  function scoreProduct(p, profile) {
    var score = 0;
    FACETS.forEach(function (facet) {
      var values = p[facet] || [];
      for (var i = 0; i < values.length; i++) {
        score += profile[facet][values[i]] || 0;
      }
    });
    var tags = p.tags || [];
    for (var t = 0; t < tags.length; t++) {
      score += (profile.tags[tags[t]] || 0) * TAG_FACTOR;
    }
    return score;
  }

  // Personalized picks: score the pool (excluding what the visitor has already
  // favorited or recently viewed), keep the positives top-down, then top up with
  // generic newest-first prints so the column always fills. With no profile yet,
  // fall back to the generic newest-first order outright.
  function pickPersonalized(pool, favSet, recentSet, poolByHandle) {
    var seen = {};
    favSet.forEach(function (h) {
      seen[h] = true;
    });
    recentSet.forEach(function (h) {
      seen[h] = true;
    });

    var candidates = pool.filter(function (p) {
      return !seen[p.handle];
    });

    var profile = buildProfile(favSet, recentSet, poolByHandle);
    if (!profile) {
      // Pool arrives newest first from the server, so this is the generic
      // "newest from the fallback pool" recommendation.
      return candidates.slice(0, MAX_CARDS);
    }

    // Stable sort by score desc; pool order (newest first) breaks ties.
    var scored = candidates.map(function (p, i) {
      return { p: p, score: scoreProduct(p, profile), i: i };
    });
    scored.sort(function (a, b) {
      return b.score - a.score || a.i - b.i;
    });

    var picks = [];
    var used = {};
    // First the products the profile actually matched.
    scored.forEach(function (entry) {
      if (picks.length >= MAX_CARDS || entry.score <= 0) {
        return;
      }
      picks.push(entry.p);
      used[entry.p.handle] = true;
    });
    // Top up with generic newest-first so the column never looks half-empty.
    for (var i = 0; i < candidates.length && picks.length < MAX_CARDS; i++) {
      if (!used[candidates[i].handle]) {
        picks.push(candidates[i]);
        used[candidates[i].handle] = true;
      }
    }
    return picks;
  }

  // ----- boot -----

  function readPool() {
    var node = document.querySelector('[data-hj-recs-pool]');
    if (!node) {
      return [];
    }
    try {
      var parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function init() {
    var section = document.querySelector('[data-hj-recs]');
    if (!section) {
      return;
    }

    // The section can cap cards per column (data-hj-max); fall back to the default.
    var attrMax = parseInt(section.getAttribute('data-hj-max'), 10);
    if (attrMax > 0) {
      MAX_CARDS = attrMax;
    }

    var pool = readPool();
    var poolByHandle = {};
    pool.forEach(function (p) {
      poolByHandle[p.handle] = p;
    });

    var favCol = section.querySelector('[data-hj-col="favorites"]');
    var rvCol = section.querySelector('[data-hj-col="recently"]');
    var picksCol = section.querySelector('[data-hj-col="picks"]');

    // Column 1: Favorites. Re-renders on every favorites change so toggling a
    // heart anywhere (including on a card in this column) keeps it current.
    function renderFavorites() {
      if (!favCol) {
        return;
      }
      getProducts(favoriteHandles(), poolByHandle).then(function (products) {
        renderListColumn(favCol, products, pool, {
          title: 'Save prints you love',
          text: 'Tap the heart on any print to keep it here.',
          cta: 'Start here'
        });
      });
    }

    // Column 2: Recently Viewed. Static for the page load (history only grows on
    // a product visit, not here).
    function renderRecentlyViewed() {
      if (!rvCol) {
        return;
      }
      getProducts(recentHandles(), poolByHandle).then(function (products) {
        renderListColumn(rvCol, products, pool, {
          title: 'Start browsing',
          text: 'Prints you view will show up here for easy access.',
          cta: 'Explore prints'
        });
      });
    }

    // Column 3: Personalized Picks. Synchronous from the pool; re-render on a
    // favorites change so the picks react to a new favorite immediately.
    function renderPicks() {
      if (!picksCol) {
        return;
      }
      var favSet = (window.HeyJackFavorites && window.HeyJackFavorites.list ? window.HeyJackFavorites.list() : []).filter(Boolean);
      var recentSet = (window.HeyJackRecentlyViewed && window.HeyJackRecentlyViewed.list ? window.HeyJackRecentlyViewed.list() : []).filter(Boolean);
      var picks = pickPersonalized(pool, favSet, recentSet, poolByHandle);
      fillColumn(picksCol, chunkPanels(picks.map(buildCard)));
    }

    renderFavorites();
    renderRecentlyViewed();
    renderPicks();

    document.addEventListener('hjfavorites:change', function () {
      renderFavorites();
      renderPicks();
    });
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
